/**
 * DashboardLayoutEditor — Bereiche ein-/ausblenden und ordnen.
 * Mobil-first: 44-px-Ziele, keine Drag-Geste nötig (Pfeile), Schalter rechts.
 * Kein Gold: Einstellungs-Editor (DESIGN.md).
 */
import { ArrowDown, ArrowUp, Lock, RotateCcw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { layoutRows } from '@/lib/dashboardLayout';
import { useDashboardLayout } from './useDashboardLayout';

function MoveButton({ label, disabled, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex w-11 h-11 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors"
    >
      {children}
    </button>
  );
}

export default function DashboardLayoutEditor({ className }) {
  const { layout, loading, saving, isDefault, setVisible, move, reset } = useDashboardLayout();
  const rows = layoutRows(layout);

  return (
    <div className={cn('flex flex-col', className)} aria-busy={loading || saving}>
      <p className="text-sm text-slate-500">
        Wählen Sie, welche Bereiche das Dashboard zeigt und in welcher Reihenfolge. Änderungen gelten sofort.
      </p>

      <ol className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
        {rows.map((row, i) => {
          const switchId = `dash-widget-${row.id}`;
          return (
            <li key={row.id} className={cn('flex items-center gap-2 py-2', !row.visible && 'opacity-60')}>
              <span className="w-7 shrink-0 text-center font-display text-sm font-extrabold tabular-nums text-slate-400">
                {String(i + 1).padStart(2, '0')}
              </span>
              <label htmlFor={switchId} className="min-w-0 flex-1 cursor-pointer">
                <span className="block text-sm font-semibold text-slate-900 truncate">{row.label}</span>
                <span className="block text-xs text-slate-500 line-clamp-2">{row.description}</span>
              </label>
              <div className="flex items-center shrink-0 -mr-1">
                <MoveButton label={`${row.label} nach oben`} disabled={i === 0 || loading} onClick={() => move(row.id, -1)}>
                  <ArrowUp className="w-4 h-4" />
                </MoveButton>
                <MoveButton label={`${row.label} nach unten`} disabled={i === rows.length - 1 || loading} onClick={() => move(row.id, 1)}>
                  <ArrowDown className="w-4 h-4" />
                </MoveButton>
              </div>
              {row.locked ? (
                <span className="inline-flex w-11 justify-center text-slate-400" title="Immer sichtbar" aria-label="Immer sichtbar">
                  <Lock className="w-4 h-4" />
                </span>
              ) : (
                <Switch
                  id={switchId}
                  checked={row.visible}
                  disabled={loading}
                  onCheckedChange={(v) => setVisible(row.id, v)}
                  aria-label={`${row.label} anzeigen`}
                />
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-400">{saving ? 'Wird gespeichert …' : isDefault ? 'Standard-Layout' : 'Angepasstes Layout'}</p>
        <button
          type="button"
          onClick={reset}
          disabled={isDefault || loading}
          className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Standard wiederherstellen
        </button>
      </div>
    </div>
  );
}
